import { useState } from "react";
import { FileText, Receipt, Wallet, History } from "lucide-react";
import { Field, StatusBadge, EmptyState, formatDate } from "@/components/admin/ui";
import { centsToDisplay } from "@/lib/money";
import QuoteTimeline from "@/components/admin/QuoteTimeline";

const TABS = [
  { key: "overview", label: "Overview" },
  { key: "quote", label: "Quote" },
  { key: "invoices", label: "Invoices" },
  { key: "payments", label: "Payments" },
  { key: "activity", label: "Activity" },
];

const labelClass = "text-xs font-bold uppercase tracking-[0.14em] text-slate-400 dark:text-slate-500";

export const BillingDetail = ({ orgName, billing = {}, quote, solutionItems = [], onViewQuote, testId = "billing-detail" }) => {
  const [tab, setTab] = useState("overview");
  const recurringTotal = quote?.totals?.recurring_total_cents ?? billing.recurring_amount_cents;
  const oneTimeTotal = quote?.totals?.one_time_subtotal_cents;
  const initialTotal = quote?.totals?.initial_total_cents ?? billing.initial_amount_cents;

  return (
    <div data-testid={testId}>
      <div className="mb-6 flex flex-wrap gap-2 border-b border-slate-200 pb-3 dark:border-white/10" data-testid={`${testId}-tabs`}>
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            data-testid={`${testId}-tab-${t.key}`}
            className={`rounded-full px-4 py-1.5 text-sm font-bold transition-colors duration-200 ${tab === t.key ? "bg-[#0052FF] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-white/5 dark:text-slate-300 dark:hover:bg-white/10"}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="space-y-6">
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Organization" value={orgName} testId="billing-org-name" />
            <div>
              <p className={labelClass}>Quote ID</p>
              {billing.quote_id ? (
                <button type="button" onClick={onViewQuote} data-testid="billing-quote-id-link" className="mt-1 block text-sm font-bold text-blue-600 hover:underline dark:text-blue-400">{billing.quote_id}</button>
              ) : (
                <p className="mt-1 text-sm font-semibold text-slate-900 dark:text-white">—</p>
              )}
            </div>
            <Field label="Billing Frequency" value={billing.billing_frequency} testId="billing-frequency" />
            <Field label="Payment Terms" value={quote?.payment_terms || billing.payment_terms} testId="billing-payment-terms" />
            <Field label="Billing Contact" value={billing.billing_contact_name || quote?.contact_name} testId="billing-contact-name" />
            <Field label="Contact Email" value={billing.billing_contact_email || quote?.contact_email} testId="billing-contact-email" />
            <div>
              <p className={labelClass}>Quote Status</p>
              <div className="mt-1.5"><StatusBadge status={quote?.effective_status || "—"} testId="billing-quote-status" /></div>
            </div>
            <Field label="Quote Accepted" value={formatDate(billing.accepted_at || quote?.accepted_at)} testId="billing-accepted-at" />
            <div>
              <p className={labelClass}>Billing Status</p>
              <div className="mt-1.5"><StatusBadge status={billing.payment_status || "Not Configured"} testId="billing-payment-status" /></div>
            </div>
            <Field label="Next Payment" value={formatDate(billing.next_billing_date)} testId="billing-next-payment" />
          </div>

          <div>
            <p className={labelClass}>Plan / Solutions</p>
            {solutionItems.length === 0 ? (
              <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">No line items recorded.</p>
            ) : (
              <div className="mt-2 space-y-2" data-testid="billing-solution-items">
                {solutionItems.map((item) => (
                  <div key={item.item_id} className="flex items-center justify-between rounded-xl border border-slate-200 px-4 py-2.5 text-sm dark:border-white/10">
                    <span className="font-semibold text-slate-800 dark:text-slate-200">{item.description}{item.quantity > 1 ? ` × ${item.quantity}` : ""}</span>
                    <span className="text-slate-500 dark:text-slate-400">{centsToDisplay(item.unit_price_cents)}{item.billing_type === "one_time" ? " one-time" : " /mo"}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="grid gap-5 sm:grid-cols-3">
            <Field label="Recurring Charges" value={centsToDisplay(recurringTotal)} testId="billing-recurring-charges" />
            <Field label="One-Time Charges" value={oneTimeTotal != null ? centsToDisplay(oneTimeTotal) : "—"} testId="billing-onetime-charges" />
            <Field label="Initial Total" value={centsToDisplay(initialTotal)} testId="billing-initial-total" />
          </div>

          {billing.quote_id && (
            <button type="button" onClick={onViewQuote} data-testid="billing-view-full-quote-btn" className="inline-flex items-center gap-2 rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-bold text-slate-700 transition-colors duration-200 hover:bg-slate-50 dark:border-white/15 dark:text-slate-200 dark:hover:bg-white/10">
              <FileText size={15} /> View Full Quote
            </button>
          )}
        </div>
      )}

      {tab === "quote" && (
        quote ? (
          <div className="space-y-5" data-testid="billing-quote-tab">
            <div className="flex flex-wrap items-center gap-3">
              <StatusBadge status={quote.effective_status} testId="billing-quote-tab-status" />
              <span className="text-sm text-slate-500 dark:text-slate-400">Valid until {formatDate(quote.valid_until)}</span>
            </div>
            <div className="grid gap-5 sm:grid-cols-2">
              <Field label="Quote ID" value={quote.quote_id} testId="billing-quote-tab-id" />
              <Field label="Company" value={quote.company} testId="billing-quote-tab-company" />
              <Field label="Initial Total" value={centsToDisplay(quote.totals?.initial_total_cents)} testId="billing-quote-tab-initial" />
              <Field label="Recurring Total" value={centsToDisplay(quote.totals?.recurring_total_cents)} testId="billing-quote-tab-recurring" />
            </div>
            <button type="button" onClick={onViewQuote} data-testid="billing-quote-tab-view-btn" className="inline-flex items-center gap-2 rounded-xl bg-[#0052FF] px-4 py-2.5 text-sm font-bold text-white">
              <FileText size={15} /> View Full Quote
            </button>
          </div>
        ) : (
          <EmptyState icon={FileText} title="No quote linked." message="This billing record does not have a linked quote." testId="billing-quote-empty" />
        )
      )}

      {tab === "invoices" && (
        <EmptyState icon={Receipt} title="No invoices yet." message="Invoices will appear here once payment processing is connected for Nova Opsis billing." testId="billing-invoices-empty" />
      )}

      {tab === "payments" && (
        <EmptyState icon={Wallet} title="No payments yet." message="Payment history and payment methods will appear here once a payment provider is connected." testId="billing-payments-empty" />
      )}

      {tab === "activity" && (
        quote?.quote_events?.length ? (
          <QuoteTimeline events={quote.quote_events} />
        ) : (
          <EmptyState icon={History} title="No activity yet." message="Billing activity for this organization will appear here." testId="billing-activity-empty" />
        )
      )}
    </div>
  );
};
